import React, { forwardRef, useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { useDitto, checkIsImage } from '@ow/ditto-core'
import { getWaterMarkDefsItemInfo } from '@ow/utils'
import '@uppy/core/dist/style.css'
import '@uppy/progress-bar/dist/style.css'
import MediaItem from './comps/MediaItem'
import UploadFile from './comps/UploadFile'

const transformCode = (waterMarkInfo) => {
  try {
    return btoa(
      unescape(
        encodeURIComponent(
          `["${waterMarkInfo[0] || ''}","${waterMarkInfo[1] || ''}","${
            waterMarkInfo[2] || ''
          }"]`
        )
      )
    )
  } catch (e) {
    return ''
  }
}

const Container = styled.div`
  .status-icon {
    width: 2rem;
    height: 2rem;
  }
  .status-area {
    width: 100px;
    height: 100px;
  }
`

const UploadWrapper = styled.div`
  margin-top: 10px;
  .limit-text {
    color: var(--text-gray-400);
    font-size: 0.875rem;
    line-height: 1.5rem;
  }
`
const ToolTip = styled.div`
  position: relative;
  display: inline-block;
  padding: 4px 10px 4px 0;
  .tips {
    position: absolute;
    left: 100%;
    top: 10px;
    background: rgba(0, 0, 0, 0.8);
    color: #fff;
    border-radius: 4px;
    padding: 10px;
    z-index: 1000;
    display: none;
    width: 300px;
    p {
      word-break: break-all;
    }
  }
  &:hover {
    .tips {
      display: block;
    }
  }
`
const Render = function (
  { opts = {}, value, waterMarkDefs, data: rowData, onChange, formControl },
  ref
) {
  const {
    title,
    description,
    fileTypeMap = [],
    allowedTypes,
    render,
    serverAuth,
    uploadQuantities,
    maxSize,
  } = opts
  const { upload: onClickUpload, addWatermarkToImage } = useDitto()
  const [lnglat, setLnglat] = useState('')
  const [showValue, setShowValue] = useState([])

  const [uploadStatus, setUploadStatus] = useState(null)

  const deleteFile = (file, index) => {
    const newfileInfo = [...value]
    newfileInfo.splice(index, 1)
    setUploadStatus('deleted')
    if (onChange) {
      onChange(newfileInfo)
    }
  }

  const renderErrorStatus = () => {
    if (uploadStatus === 'error') {
      return '上传失败!'
    }
    if (uploadStatus === 'deleted') {
      return '删除成功!'
    }
    if (uploadStatus === 'size-over') {
      return '文件大小超出限制!'
    }
    if (uploadStatus === 'limit') {
      return `上限${uploadQuantities}个!`
    }
    return ''
  }

  const renderBtnText = () => {
    if (uploadQuantities) {
      return `上限${uploadQuantities}个${
        maxSize ? `，每个${maxSize}M以内` : ``
      }`
    }
    return `${maxSize ? maxSize + 'M以内' : ''}`
  }

  useEffect(() => {
    if (
      uploadStatus === 'limit' ||
      uploadStatus === 'error' ||
      uploadStatus === 'deleted' ||
      uploadStatus === 'size-over'
    ) {
      setTimeout(() => {
        setUploadStatus(null)
      }, 3000)
    }
  }, [uploadStatus])
  useEffect(() => {
    const { getValues } = formControl
    if (getValues) {
      setLnglat(getValues('lnglat'))
    }
  }, [])

  useEffect(() => {
    if (waterMarkDefs && addWatermarkToImage) {
      const arrs = (value || []).map((item) => {
        const url = item.src || item.link
        if (!url) {
          return item
        }
        let src = url
        const position = url && url.indexOf('?Expires=') // 对非表单新建的数据特殊处理
        if (
          url &&
          (url.indexOf('ow-prod.oss-') > -1 || url.indexOf('ow-prod-cdn') > -1)
        ) {
          src = `${
            position === -1 ? url : url.slice(0, position)
          }?x-oss-process=image/resize,h_170,m_lfit`
        }
        const isImage = checkIsImage(item)
        const { extInfo } = item
        const finalData = { ...rowData, extInfo }
        const waterMarkInfo = getWaterMarkDefsItemInfo(waterMarkDefs, finalData)

        const endPosition = url.indexOf('?Expires=')
        const link = item.src || item.link
        return {
          ...item,
          waterMarkInfo:
            waterMarkInfo && waterMarkInfo.length ? waterMarkInfo : null,
          srcWatermark:
            typeof btoa !== 'undefined' &&
            isImage &&
            waterMarkInfo &&
            waterMarkInfo.length > 0
              ? addWatermarkToImage(
                  encodeURIComponent(
                    endPosition === -1 ? link : link.slice(0, endPosition)
                  ),
                  encodeURIComponent(transformCode(waterMarkInfo)),
                  10,
                  'white',
                  0,
                  true
                )
              : src,
        }
      })
      setShowValue(arrs)
    } else {
      setShowValue(value)
    }
  }, [value])

  return (
    <div className="flex flex-1 p-1 w-full flex-col">
      <div>
        {fileTypeMap.length > 0 && (
          <ToolTip>
            {title}
            <div className="tips">
              <p>支持类型及格式：</p>
              {fileTypeMap
                .filter((fileTypeItem) => opts[fileTypeItem.type])
                .map((item) => {
                  const { typeList, label } = item
                  const selectList = typeList
                    .filter((type) => allowedTypes.includes(type))
                    .join('、')
                  return <p>{`${label}: ${selectList}`}</p>
                })}
            </div>
          </ToolTip>
        )}
        <div className="text-sm text-gray-400">{description}</div>
      </div>
      <UploadWrapper>
        <Container className="w-full -mt-2" ref={ref}>
          <span
            className="limit-text mt-2 text-primary"
            style={{ fontSize: '0.75rem' }}
          >
            {renderBtnText()}
          </span>
          <div className="media-box w-full flex flex-row items-center flex-wrap gap-2 mt-2">
            <UploadFile
              opts={opts}
              files={value || []}
              selectedfiles={value || []}
              uploadStatus={uploadStatus}
              updateUploadStatus={(v) => {
                setUploadStatus(v)
              }}
              updateSelectedfiles={(e) => {
                const links = e.map((v) => {
                  return { ...v, serverAuth }
                })

                onChange(links.length ? links : null)
              }}
              onClickUpload={onClickUpload}
              rowData={rowData}
              formControl={formControl}
            />

            {(value || []).map((item, index) => {
              const { src } = item || {}
              if (src) {
                return (
                  <MediaItem
                    files={(showValue || []).map((v) => {
                      return { ...v, filename: v.filename, src: v.src, lnglat }
                    })}
                    render={render}
                    key={item.src}
                    item={item}
                    index={index}
                    deleteFile={deleteFile}
                  />
                )
              }
              return null
            })}
          </div>
          <div className="w-full h-full flex flex-col relative cursor-pointer">
            <div style={{ marginTop: '.375rem', fontSize: '.75rem' }}>
              <span className="text-error-500 font-semibold">
                {renderErrorStatus()}
              </span>
            </div>
          </div>
        </Container>
      </UploadWrapper>
    </div>
  )
}
export default forwardRef(Render)
